import { findRegimen, type RegimenLookup } from "@/data/meal-plan";
import { createEmptyWeek, EMPTY_DAY, type DayOfWeek, type WeekPlan } from "@/types/meal-plan-state";

const WEEK_DAYS: DayOfWeek[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * `{ optionId: string, count }`, or the legacy `{ optionIndex: number, count }`
 * that `migrateWeekPlan` converts (it skips skipped groups and unknown meal or
 * group indices, so stored and re-saved weeks can still hold it).
 */
function isValidSelection(sel: unknown): boolean {
  if (!isRecord(sel)) return false;
  const { optionId, optionIndex, count } = sel;
  const hasOption = typeof optionId === "string" || (typeof optionIndex === "number" && Number.isFinite(optionIndex));
  return hasOption && typeof count === "number" && Number.isFinite(count) && count >= 0;
}

/** Path (relative to the choice group) of the first problem, or null. */
function choiceGroupError(cg: unknown): string | null {
  if (!isRecord(cg)) return "";
  if (!Array.isArray(cg.selections)) return ".selections";
  const bad = cg.selections.findIndex((sel) => !isValidSelection(sel));
  if (bad !== -1) return `.selections[${bad}]`;
  if (typeof cg.skipped !== "boolean") return ".skipped";
  // Toggling a slot past the end leaves holes, which JSON stores as null.
  if (cg.eaten != null && !(Array.isArray(cg.eaten) && cg.eaten.every((e) => e === null || typeof e === "boolean"))) {
    return ".eaten";
  }
  return null;
}

/** Absent, or `{ [mealIndex]: string }`. */
function isValidIndexedStrings(value: unknown): boolean {
  if (value == null) return true;
  if (!isRecord(value)) return false;
  return Object.entries(value).every(([key, v]) => /^\d+$/.test(key) && typeof v === "string");
}

/** Path (relative to the day) of the first problem, or null. */
function dayPlanError(day: unknown, isKnownRegimen: (id: string) => boolean): string | null {
  if (!isRecord(day)) return "";
  const { regimenId, meals } = day;
  if (!(regimenId === null || (typeof regimenId === "string" && isKnownRegimen(regimenId)))) return ".regimenId";
  if (meals !== null) {
    if (!Array.isArray(meals)) return ".meals";
    for (let mi = 0; mi < meals.length; mi++) {
      const meal: unknown = meals[mi];
      if (!Array.isArray(meal)) return `.meals[${mi}]`;
      for (let ci = 0; ci < meal.length; ci++) {
        const error = choiceGroupError(meal[ci]);
        if (error !== null) return `.meals[${mi}][${ci}]${error}`;
      }
    }
  }
  if (!isValidIndexedStrings(day.mealNotes)) return ".mealNotes";
  if (!isValidIndexedStrings(day.mealMenus)) return ".mealMenus";
  return null;
}

/**
 * Where `plan` first breaks the `WeekPlan` shape the UI reads, as a path
 * ("tue.meals[0][1].selections"), or null when it's valid. Checks what
 * rendering relies on, no more: all seven days; `regimenId` null or a known
 * regimen; `meals` null or arrays of choice groups `{ selections, skipped,
 * eaten? }`; `mealNotes`/`mealMenus` absent or strings by meal index. Extra
 * keys are tolerated, and so are legacy `{ optionIndex, count }` selections.
 */
export function weekPlanError(plan: unknown, lookup: RegimenLookup = findRegimen): string | null {
  if (!isRecord(plan)) return "plan";
  const isKnownRegimen = (id: string) => lookup(id) !== undefined;
  for (const day of WEEK_DAYS) {
    const error = dayPlanError(plan[day], isKnownRegimen);
    if (error !== null) return `${day}${error}`;
  }
  return null;
}

export function isValidWeekPlan(plan: unknown, lookup: RegimenLookup = findRegimen): plan is WeekPlan {
  return weekPlanError(plan, lookup) === null;
}

/**
 * A stored week the UI can open: every day failing validation (bad data saved
 * before writes were validated) is reset to empty, and logged. The same
 * object comes back when every day is valid.
 *
 * Any regimen id passes here: a day an unknown regimen (one a newer bundle
 * knows) already renders as unplanned, and resetting it would let this tab's
 * next save erase it.
 */
export function sanitizeWeekPlan(plan: unknown): WeekPlan {
  if (!isRecord(plan)) {
    console.error("Unreadable stored week, showing it empty", plan);
    return createEmptyWeek();
  }
  let result = plan as WeekPlan;
  for (const day of WEEK_DAYS) {
    const error = dayPlanError(plan[day], () => true);
    if (error === null) continue;
    console.error(`Invalid stored day at ${day}${error}, resetting it to empty`);
    result = { ...result, [day]: { ...EMPTY_DAY } };
  }
  return result;
}
